import React from "react";
import { useTranslation } from "react-i18next";
import "./workspace.css";

/**
 * The three layers of the content model, made navigable.
 *
 * Bottom: the document bases, where the bytes actually live.
 * Middle: the organization's taxonomy and folder plan — what a document is, and where the company files it.
 * Top: a person's own folders.
 *
 * They are lenses, not separate screens: the SAME documents are being looked at from a different angle
 * each time, and whichever lens is active, the other two show up as chips on every row. Personal is the
 * default because it is the only one that is already populated on somebody's first day.
 */
export const LAYERS = {
  workspace: "workspace",
  taxonomy: "taxonomy",
  bases: "bases",
};

export default function LayerSwitcher({ value, onChange, showOrgLayers = true }) {
  const { t } = useTranslation();

  // Without the content-governance licence there is no taxonomy or folder plan to look through, so the
  // switcher collapses to nothing rather than offering two lenses that would open empty.
  if (!showOrgLayers) return null;

  const items = [
    {
      key: LAYERS.workspace,
      label: t("workspace.layerMine", { defaultValue: "My Workspace" }),
      icon: <PersonIcon />,
    },
    {
      key: LAYERS.taxonomy,
      label: t("workspace.layerTaxonomy", { defaultValue: "Company" }),
      icon: <TagIcon />,
    },
    {
      key: LAYERS.bases,
      label: t("workspace.layerBases", { defaultValue: "Document bases" }),
      icon: <BaseIcon />,
    },
  ];

  const hint = {
    [LAYERS.workspace]: t("workspace.hintMine", {
      defaultValue: "Your own folders. Documents you filed or added yourself.",
    }),
    [LAYERS.taxonomy]: t("workspace.hintTaxonomy", {
      defaultValue: "How your organization files and classifies documents.",
    }),
    [LAYERS.bases]: t("workspace.hintBases", {
      defaultValue: "The archives themselves — where documents are physically stored.",
    }),
  }[value];

  return (
    <>
      <div className="ws-lens" role="tablist">
        {items.map((it) => (
          <button
            key={it.key}
            role="tab"
            aria-selected={value === it.key}
            className={`ws-lens__btn${value === it.key ? " ws-lens__btn--on" : ""}`}
            onClick={() => onChange(it.key)}
          >
            {it.icon}
            {it.label}
          </button>
        ))}
      </div>
      {hint && <div className="ws-lens__hint">{hint}</div>}
    </>
  );
}

function PersonIcon() {
  return (
    <svg className="ws-lens__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg className="ws-lens__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.6 13.4 12 22l-9-9V3h10z" /><circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}

function BaseIcon() {
  return (
    <svg className="ws-lens__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5" /><path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3" />
    </svg>
  );
}
